'use client'

import { PostWithProfile } from '@/lib/stores/usePostStore'
import { Card, CardContent } from '@/components/ui/card'
import { formatDistanceToNow } from 'date-fns'
import { cn } from "@/lib/utils"
import Link from 'next/link'
import { useMemo, useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { useUIStore } from '@/lib/stores/useUIStore'

interface PostCardProps {
  post: PostWithProfile
}

// Create a client component specifically for the like button
interface LikeButtonProps {
  liked: boolean;
  likeCount: number;
  onLike: () => void;
}

function LikeButton({ liked, likeCount, onLike }: LikeButtonProps) {
  return (
    <button 
      className={cn(
        "flex items-center gap-1 group", 
        liked ? "text-pink-500" : "text-muted-foreground hover:text-pink-500"
      )}
      onClick={onLike}
    >
      <div className={cn(
        "p-2 rounded-full", 
        liked ? "bg-pink-500/10" : "group-hover:bg-pink-500/10"
      )}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill={liked ? "currentColor" : "none"}
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-4 w-4"
        >
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      </div>
      <span className="text-xs">{likeCount}</span>
    </button>
  );
}

// Create client components for all interactive buttons
interface ShareButtonProps {
  className?: string;
}

function ShareButton({ className }: ShareButtonProps) {
  return (
    <button className={`text-muted-foreground flex items-center gap-1 group hover:text-blue-500 ${className || ''}`}>
      <div className="p-2 rounded-full group-hover:bg-blue-500/10">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="9 10 4 15 9 20"></polyline>
          <path d="M20 4v7a4 4 0 0 1-4 4H4"></path>
        </svg>
      </div>
      <span className="text-xs">0</span>
    </button>
  );
}

interface RetweetButtonProps {
  className?: string;
}

function RetweetButton({ className }: RetweetButtonProps) {
  return (
    <button className={`text-muted-foreground flex items-center gap-1 group hover:text-green-500 ${className || ''}`}>
      <div className="p-2 rounded-full group-hover:bg-green-500/10">
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="1.5" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <path d="M17 1l4 4-4 4"></path>
          <path d="M3 11V9a4 4 0 0 1 4-4h14"></path>
          <path d="M7 23l-4-4 4-4"></path>
          <path d="M21 13v2a4 4 0 0 1-4 4H3"></path>
        </svg>
      </div>
      <span className="text-xs">0</span>
    </button>
  );
}

interface BookmarkButtonProps {
  className?: string;
}

function BookmarkButton({ className }: BookmarkButtonProps) {
  return (
    <button className={`text-muted-foreground flex items-center gap-1 group hover:text-amber-500 ${className || ''}`}>
      <div className="p-2 rounded-full group-hover:bg-amber-500/10">
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="1.5" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
        </svg>
      </div>
    </button>
  );
}

export function PostCard({ post }: PostCardProps) {
  const [liked, setLiked] = useState(false)
  const [likeCount, setLikeCount] = useState(0)
  const { theme } = useUIStore()
  const contentRef = useRef<HTMLDivElement>(null)
  
  // Format the date
  const formattedDate = formatDistanceToNow(new Date(post.created_at), { addSuffix: true })
  
  // Get author display name (fallback to username) with null checks
  const authorName = post.profiles?.display_name || post.profiles?.username || 'Anonymous'
  const authorUsername = post.profiles?.username || 'user'
  
  // Generate a consistent random avatar for the user based on their ID or username
  const randomAvatar = useMemo(() => {
    // Use the user's ID or username as a seed for consistency
    const seed = post.author_id || authorUsername;
    // DiceBear provides various avatar styles: avataaars, bottts, identicon, etc.
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${seed}&backgroundColor=random`;
  }, [post.author_id, authorUsername]);
  
  // Truncate long text to prevent overflow
  const truncate = (str: string | null | undefined, length: number) => {
    if (!str || str.length <= length) return str
    return `${str.slice(0, length - 3)}...`
  }

  const handleLike = () => {
    setLiked(!liked);
    setLikeCount(liked ? likeCount - 1 : likeCount + 1);
  }

  // Process links to open in a new tab
  useEffect(() => {
    if (contentRef.current) {
      const links = contentRef.current.querySelectorAll('a')
      links.forEach(link => {
        // Make sure links open in a new tab
        link.setAttribute('target', '_blank')
        link.setAttribute('rel', 'noopener noreferrer')
        
        // Ensure the link is visible as a link
        link.classList.add('text-primary', 'underline', 'hover:text-primary/80')
      })
    }
  }, [post.content])

  // Truncate HTML content to a specific character length while preserving HTML
  const truncateHtml = (html: string, maxLength: number) => {
    if (!html) return '';
    
    // Create a temporary div to work with HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Get text content
    const textContent = tempDiv.textContent || tempDiv.innerText;
    
    // If the content is already shorter than maxLength, return it as is
    if (textContent.length <= maxLength) {
      return html;
    }
    
    // Find a suitable breakpoint (space, period, etc.)
    let breakPoint = textContent.lastIndexOf(' ', maxLength);
    if (breakPoint === -1 || breakPoint < maxLength - 30) {
      // If no good breakpoint found, just cut at maxLength
      breakPoint = maxLength;
    }
    
    // Clone the temp div for easier manipulation
    const outputDiv = tempDiv.cloneNode(true) as HTMLElement;
    const allNodes = getAllTextNodes(outputDiv);
    
    let currentLength = 0;
    let nodesToRemove = [];
    
    // Find where to cut the content
    for (let i = 0; i < allNodes.length; i++) {
      const node = allNodes[i];
      
      if (currentLength + node.length <= breakPoint) {
        // Keep this node
        currentLength += node.length;
      } else {
        // Cut this node
        const remainingLength = breakPoint - currentLength;
        if (remainingLength > 0) {
          node.textContent = node.textContent!.substring(0, remainingLength);
          currentLength += remainingLength;
        } else {
          // Mark this and all subsequent nodes for removal
          nodesToRemove.push(...allNodes.slice(i));
          break;
        }
      }
    }
    
    // Remove extra nodes
    nodesToRemove.forEach(node => {
      if (node.parentNode) {
        // Simply remove this node from its parent
        node.parentNode.removeChild(node);
      }
    });
    
    // Clean up any empty paragraphs or elements
    const cleanup = (element: HTMLElement) => {
      // Convert to array to avoid issues with live NodeList during removal
      const children = Array.from(element.children);
      
      for (const child of children) {
        // If it's an element node
        if (child.nodeType === Node.ELEMENT_NODE) {
          // First recursively clean up its children
          cleanup(child as HTMLElement);
          
          // Then check if it's empty or only has whitespace
          const hasText = child.textContent?.trim();
          const hasChildren = child.children.length > 0;
          
          if (!hasText && !hasChildren) {
            // Remove empty elements
            element.removeChild(child);
          }
        }
      }
    };
    
    // Clean up the output
    cleanup(outputDiv);
    
    return outputDiv.innerHTML;
  }
  
  // Helper function to get all text nodes
  const getAllTextNodes = (element: Node): Text[] => {
    let result: Text[] = [];
    
    const walk = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        result.push(node as Text);
      } else {
        for (let child of Array.from(node.childNodes)) {
          walk(child);
        }
      }
    };
    
    walk(element);
    return result;
  }
  
  // Helper function to check if a node has siblings
  const hasSiblings = (node: Node): boolean => {
    return node.parentNode ? node.parentNode.childNodes.length > 1 : false;
  }
  
  // Truncate content if needed
  const displayContent = useMemo(() => {
    if (!post.content) return '';

    // Process the content to ensure links are properly formatted
    let content = post.content;
    const needsTruncation = content.replace(/<[^>]*>/g, '').length > 300 || content.includes('<img');
    
    // Regular expression to identify standalone URLs (not already in anchor tags)
    const urlRegex = /(https?:\/\/[^\s<]+)/g;
    
    // First handle any standalone URLs in the content
    // Find all HTML tags
    const htmlTagRegex = /<[^>]+>/g;
    
    // Split the content by HTML tags
    const parts = content.split(htmlTagRegex);
    
    // Process each part (text between HTML tags)
    for (let i = 0; i < parts.length; i++) {
      if (parts[i].trim() && !parts[i].trim().startsWith('<')) {
        // Replace URLs with anchor tags
        parts[i] = parts[i].replace(urlRegex, (url) => {
          return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-primary underline hover:text-primary/80">${url}</a>`;
        });
      }
    }
    
    // Rebuild the content by interleaving HTML tags back in
    let formattedContent = '';
    let tags = content.match(htmlTagRegex) || [];
    for (let i = 0; i < parts.length; i++) {
      formattedContent += parts[i];
      if (i < tags.length) {
        formattedContent += tags[i];
      }
    }
    
    // Now truncate if needed
    if (needsTruncation) {
      return truncateHtml(formattedContent, 300);
    }
    
    return formattedContent;
  }, [post.content]);

  return (
    <Card className="hover:bg-accent/5 transition-colors duration-200 border-b border-x-0 rounded-none first:rounded-t-lg last:rounded-b-lg first:border-t border-x-0 shadow-none">
      <CardContent className="p-4 sm:p-6">
        <div className="flex gap-3">
          {/* Left Column - Avatar */}
          <div className="flex-shrink-0">
            <div className="overflow-hidden rounded-full border border-transparent">
              {post.profiles?.avatar_url ? (
                <img 
                  title={`Profile picture of ${authorName}`}
                  alt={authorUsername}
                  height={48}
                  width={48}
                  src={post.profiles.avatar_url}
                  className="h-10 w-10 rounded-full object-cover"
                />
              ) : (
                <img
                  title={`Avatar for ${authorName}`}
                  alt={authorUsername}
                  height={48}
                  width={48}
                  src={randomAvatar}
                  className="h-10 w-10 rounded-full object-cover bg-muted"
                />
              )}
            </div>
          </div>
          
          {/* Right Column - Content */}
          <div className="flex-1 min-w-0">
            {/* Header with name, username, time */}
            <div className="flex items-center gap-1 mb-0.5 flex-wrap">
              <Link 
                href={`/profile/${authorUsername}`} 
                className="font-semibold hover:underline"
              >
                {truncate(authorName, 20)}
              </Link>
              <Link 
                href={`/profile/${authorUsername}`}
                className="text-muted-foreground hover:underline"
              >
                @{truncate(authorUsername, 16)}
              </Link>
              <span className="text-muted-foreground">·</span>
              <span className="text-muted-foreground text-sm">{formattedDate}</span>
              
              {post.post_type && (
                <span className="ml-auto inline-flex rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                  {post.post_type}
                </span>
              )}
            </div>
            
            {/* Post content */}
            <div className="mb-3">
              {post.title && (
                <h2 className="font-bold text-base mb-1">
                  <Link href={`/dashboard/post/${post.id}`} className="hover:underline">
                    {post.title}
                  </Link>
                </h2>
              )}
              
              <div 
                ref={contentRef}
                className={`post-content text-sm text-foreground leading-normal prose prose-sm max-w-none ${theme === 'dark' ? 'dark:prose-invert' : ''}`}
                dangerouslySetInnerHTML={{ __html: displayContent }}
              />
              
              {post.content && post.content.replace(/<[^>]*>/g, '').length > 300 && (
                <div className="mt-2">
                  <Link 
                    href={`/dashboard/post/${post.id}`}
                    className="text-primary hover:underline text-sm font-medium inline-flex items-center"
                  >
                    Read more
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      className="ml-1 h-3 w-3"
                    >
                      <path d="M5 12h14"></path>
                      <path d="m12 5 7 7-7 7"></path>
                    </svg>
                  </Link>
                </div>
              )}
            </div>
            
            {/* Action buttons */}
            <div className="flex items-center space-x-6 max-w-md mx-auto sm:mx-0">
              <ShareButton />
              <LikeButton 
                liked={liked} 
                likeCount={likeCount}
                onLike={handleLike} 
              />
              <RetweetButton />
              <BookmarkButton />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
} 